<script>
import WideIconTile from '@/components/WideIconTile'
import FloatingLabelTextInput from '@/components/FloatingLabelTextInput'
import Separator from '@/components/Separator'
import DOMUtils from '@/utils/DOMUtils'
import StringUtils from '@/utils/stringUtils'
import DateUtils from '@/utils/dateUtils'
import EnvironmentUtils from '@/utils/environmentUtils'
import ActualitiesService from '@/services/actualities'
import { waitUntilUserIsFetched } from '@/stores/index'
import Fuse from 'fuse.js'

const REF_EDIT_ACTUALITIES_MENU_INDEX_VIEW = 'edit-actualities-menu-index-view'

export default
{
    name: 'edit-actualities-menu-index',

    beforeRouteEnter (to, from, next)
    {
        next(vm =>
        {
            let tiles = vm.editActualitiesMenuIndex.querySelectorAll('.pop')
            
            tiles.forEach(tile =>
            {
                DOMUtils.animateCSS(tile, 'zoomIn', '0.4s')
            })
        })
    },

    created ()
    {
        waitUntilUserIsFetched().then(async () =>
        {
            const actualities = await ActualitiesService.fetchActualities(EnvironmentUtils.cityId, this.$store.state.user.token, {from: 0, number: 1000, includeDrafts: true, excludeContent: true})
            this.$store.commit('actualities/updateActualityList', actualities)
        })

        const fuseOptions =
        {
            shouldSort: true,
            threshold: 0.5,
            location: 0,
            distance: 100,
            maxPatternLength: 32,
            minMatchCharLength: 1,
            keys: [ 'title' ]
        }

        this.fuse = new Fuse(this.actualities, fuseOptions)
    },

    render () 
    {
        return (
            <div id='edit-actualities-menu-index' ref={REF_EDIT_ACTUALITIES_MENU_INDEX_VIEW}>
                <WideIconTile id='add-actuality' class='button-tile pop' on-click={this.onAddActualityTileClicked} title={ 'publier une nouvelle actualité' }
                    icon={ require('@/assets/img/plus.svg') } inline-svg={true} />
                
                <div id='publied-actualities-card' class='pop'>
                    <FloatingLabelTextInput id='actuality-search-field' class='light-gray-style' type='text' placeholder='Rechercher une actualité'
                        icon={require('@/assets/img/magnifying-glass-thin.svg')} placeholder-disappear-on-text={true} v-model={this.actualitySearchValue} />
                    
                    <div id='actuality-cards'>
                        {
                            this.displayedActualities.map((actuality, i) =>
                            {
                                return (
                                    <div on-click={() => this.onActualityCardClicked(actuality)}>
                                        <Separator  v-show={i > 0} />

                                        <div class='actuality-card'>
                                            <div class='actuality-card-information'>
                                                <img class='actuality-image' src={actuality.imageUrl || require('@/assets/img/pencil-illustration.svg')} />
                                                <div class='actuality-header'>
                                                    <p class='actuality-title'>{ actuality.title }</p>
                                                    <div class='actuality-subtitle-container'>
                                                        <p class='actuality-subtitle'>
                                                            Créée le { DateUtils.dateToString(actuality.date, 'iso', 'fr') }
                                                        </p>
                                                        {
                                                            actuality.isDraft &&
                                                            (
                                                                <div class='label'>Brouillon</div>
                                                            ),
                                                            !actuality.isPublished && actuality.scheduledPublishingDate &&
                                                            (
                                                                <div class='label'>Programmée pour le {new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', hour: 'numeric', minute: 'numeric', year: '2-digit' }).format(new Date(actuality.scheduledPublishingDate))}</div>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>
            </div>
        )
    },

    data () 
    {
        return {
            fuse: null,
            actualitySearchValue: ''
        }
    },

    computed:
    {
        actualities ()
        {
            return this.$store.state.actualities.actualityList
        },

        displayedActualities ()
        {
            if (StringUtils.isEmptyString(this.actualitySearchValue))
            {
                return this.actualities
            }

            return this.fuse.search(this.actualitySearchValue)
        },

        editActualitiesMenuIndex ()
        {
            return this.$refs[REF_EDIT_ACTUALITIES_MENU_INDEX_VIEW]
        }
    },

    mounted ()
    {
    },

    watch:
    {
    },

    methods:
    {
        onAddActualityTileClicked ()
        {
            this.$router.push({ path: 'add', append: true })
        },

        async onActualityCardClicked (actuality)
        {
            this.$router.push({ path: `edit/${actuality.id}`, append: true })
        }
    },

    components:
    {
        WideIconTile,
        FloatingLabelTextInput,
        Separator
    }
}
</script>

<style lang='less'>
@import '~@/styles/main.less';

#edit-actualities-menu-index
{
    display: flex;
    flex-direction: column;
    padding: 0 3vw 0 3vw;

    .OnDesktop({
        padding-left: 10vw;
        padding-right: 10vw;
    });

    #add-actuality
    {
        border-radius: 15px;
        margin: 0;
        padding-left: 6%;
        padding-right: 6%;

        .icon
        {
            .WidthHeight(15px);
            fill: var(--theme-accent-color);
        }
    }

    #publied-actualities-card
    {
        background-color: var(--theme-content-background-color);
        border-radius: 15px;
        margin-top: 2vh;
        display: flex;
        flex-flow: column;
        padding: 3vh 3vh 2vh 3vh;
        box-shadow: 0px 3px 10px rgba(0, 0, 0, 0.05);

        height: 67vh;

        .OnTablet({
            height: 68vh;
        });

        .OnDesktop({
            height: 75vh;
        });

        #actuality-search-field
        {
            margin-bottom: 10px;
            
            .OnMobile({
                height: 13vw;
            });
        }

        #actuality-cards
        {
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;

            .actuality-card
            {
                display: flex;
                flex-direction: line;
                align-items: center;
                padding: 3vw 1vw;
                cursor: pointer;

                .OnDesktop({
                    padding: 1.5vw 1vw;
                });

                .actuality-card-information
                {
                    display: flex;
                    flex-direction: row;
                    align-items: center;

                    .actuality-image
                    {
                        object-fit: cover;
                        border-radius: 7px;
                        width: 17vw;
                        height: 12vw;

                        .OnTablet({
                            width: 11vw;
                            height: 8vw;
                        });

                        .OnDesktop({
                            width: 6vw;
                            height: 4vw;
                        });
                    }

                    .actuality-header
                    {
                        display: flex;
                        flex-direction: column;
                        margin-left: 5vw;

                        .OnTablet({ margin-left: 4vw; });
                        .OnDesktop({ margin-left: 2vw; });

                        .actuality-title
                        {
                            font-family: Nunito;
                            font-weight: 500;
                            font-size: 4.5vw;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                            overflow: hidden;

                            .CapitalizeFirstLetter();

                            .OnTablet({
                                font-size: 3vw;
                            });

                            .OnDesktop({
                                font-size: 1vw;
                            });
                        }

                        .actuality-subtitle-container
                        {
                            display: flex;
                            flex-direction: row;
                            padding-top: 7px;
                            align-items: center;

                            .label
                            {
                                font-size: 1.5vh;
                                background: orange;
                                color: white;
                                padding: 5px;
                                border-radius: 3px;
                                margin-left: 10px;
                            }

                            .actuality-subtitle
                            {
                                color: var(--theme-text-color-extra-light);
                                font-size: 3.5vw;

                                .CapitalizeFirstLetter();

                                .OnTablet({
                                    font-size: 2vw;
                                });

                                .OnDesktop({
                                    font-size: 0.7vw;
                                });
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/views/EditActualitiesMenuIndex.vue